/**
 * @fileoverview Removal Plan - ピース削除実行計画システム
 * 責務：削除対象ピースから段階的な削除実行計画を作成
 * マッチピース優先、特殊ピース連鎖の順序制御を行う
 */

import { resolveSpecialEffect } from './specials/system.js';

// =============================================================================
// 内部ユーティリティ関数群
// =============================================================================

/**
 * ピースのキーを生成（参照同一性に依存しない）
 */
function getPieceKey(piece) {
  if (!piece?.position) return '';
  const [r, c] = piece.position;
  return `${r}-${c}`;
}

/**
 * plan.removesからピースを安全に取得するヘルパー関数
 * @param {Object} board - Boardインスタンス
 * @param {Object} plan - 効果プラン
 * @returns {Array} 影響を受けるピース配列
 */
function getAffectedPieces(board, plan) {
  return (plan.removes || [])
    .map((pos) => {
      return board.getPiece(pos.row, pos.col);
    })
    .filter(Boolean);
}

/**
 * 統一された効果解決関数
 */
function resolve(board, piece) {
  if (!piece?.position || !piece.specialType) {
    return { removes: [], transforms: [], creates: [], score: 0, triggers: [] };
  }
  return resolveSpecialEffect(board, piece);
}

/**
 * マッチピース優先ルールで削除実行計画を作成する
 * マッチピースがあれば優先処理、その後特殊ピース連鎖処理
 *
 * @param {Object} board - Boardインスタンス
 * @param {Array} matchPieces - 通常マッチピース配列
 * @param {Array} specialPieces - 特殊ピース配列
 * @param {Array} allAffectedPieces - 既に影響を受けたピース配列
 * @returns {Object} { waves: Array } - 段階的削除実行計画
 */
function createRemovalPlan(board, matchPieces, specialPieces, allAffectedPieces = []) {
  const waves = [];
  const allAffectedKeySet = new Set(allAffectedPieces.filter(Boolean).map(getPieceKey));

  let currentMatchPieces = (matchPieces || []).filter(Boolean);
  let currentSpecialPieces = (specialPieces || []).filter(Boolean);
  let waveIndex = 0;

  // マッチ優先ルールでの継続判定
  while (currentMatchPieces.length > 0 || currentSpecialPieces.length > 0) {
    let result;
    if (currentMatchPieces.length > 0) {
      result = processNormalMatchWave(currentMatchPieces, currentSpecialPieces, allAffectedKeySet);
    } else {
      result = processSpecialEffectWave(currentSpecialPieces, board, allAffectedKeySet, waveIndex);
    }
    waves.push(result.wave);
    currentMatchPieces = [];
    currentSpecialPieces = result.nextSpecialPieces;
    waveIndex++;
  }

  return { waves };
}

/**
 * 通常マッチの削除Wave処理
 */
function processNormalMatchWave(matchPieces, specialPieces, allAffectedKeySet) {
  const removes = [];
  const pendingCreations = []; // 削除後に生成する特殊ピース情報
  const nextSpecialPieces = specialPieces; // 特殊ピースはそのまま持ち越し

  // マッチピースを削除対象に追加し、同時にキーセットに登録
  for (const p of matchPieces) {
    if (!p?.position) continue;
    removes.push({ row: p.position[0], col: p.position[1] });
    allAffectedKeySet.add(getPieceKey(p));

    // 特殊ピース生成予約がある場合は記録
    if (p._pendingCreation) {
      pendingCreations.push({
        row: p.position[0],
        col: p.position[1],
        specialPiece: p._pendingCreation.specialPiece,
        matchedColors: p._pendingCreation.matchedColors,
      });
    }
  }

  return {
    wave: { triggers: [], removes, pendingCreations },
    nextSpecialPieces,
  };
}

/**
 * 特殊効果の削除Wave処理
 */
function processSpecialEffectWave(specialPieces, board, allAffectedKeySet, waveIndex) {
  const context = createSpecialWaveContext(board, allAffectedKeySet, waveIndex, specialPieces);

  for (const piece of specialPieces) {
    processSpecialEffectPiece(piece, context);
  }

  return finalizeSpecialWaveContext(context);
}

function createSpecialWaveContext(board, allAffectedKeySet, waveIndex, currentSpecialPieces) {
  const currentWaveSpecialKeys = new Set(
    (currentSpecialPieces || []).map((piece) => getPieceKey(piece)).filter(Boolean),
  );

  return {
    board,
    allAffectedKeySet,
    triggers: [],
    removeSet: new Set(),
    transformMap: new Map(),
    nextSpecialPieces: [],
    nextSpecialKeys: new Set(),
    waveIndex,
    currentWaveSpecialKeys,
  };
}

function processSpecialEffectPiece(piece, context) {
  const { board, allAffectedKeySet, transformMap, removeSet, nextSpecialPieces, triggers } =
    context;
  const [centerRow, centerCol] = piece.position;
  const centerKey = getPieceKey(piece);
  const plan = resolve(board, piece);

  const trigger = buildSpecialTrigger(piece, centerRow, centerCol);
  triggers.push(trigger);

  applyTransforms(plan.transforms, transformMap, allAffectedKeySet);

  const allAffected = getAffectedPieces(board, plan);
  trigger.affectedPositions = mapAffectedPositions(allAffected);

  const newAffectedPieces = filterNewAffectedPieces(allAffected, allAffectedKeySet);

  routeRemovalEffects(
    newAffectedPieces,
    centerKey,
    nextSpecialPieces,
    removeSet,
    allAffectedKeySet,
    context,
  );

  ensureCenterRemoval(centerKey, transformMap, removeSet);
}

function buildSpecialTrigger(piece, row, col) {
  return {
    action: 'special:triggered',
    row,
    col,
    specialType: piece.specialType,
    affectedPositions: [],
  };
}

function applyTransforms(transforms = [], transformMap, allAffectedKeySet) {
  if (!Array.isArray(transforms) || transforms.length === 0) {
    return;
  }

  for (const transform of transforms) {
    const key = `${transform.row}-${transform.col}`;
    transformMap.set(key, transform.to);
    allAffectedKeySet.add(key);
  }
}

function mapAffectedPositions(pieces) {
  return pieces
    .filter((piece) => piece?.position)
    .map((piece) => ({
      row: piece.position[0],
      col: piece.position[1],
    }));
}

function filterNewAffectedPieces(pieces, allAffectedKeySet) {
  return pieces.filter((piece) => {
    const key = getPieceKey(piece);
    return key && !allAffectedKeySet.has(key);
  });
}

function routeRemovalEffects(
  pieces,
  centerKey,
  nextSpecialPieces,
  removeSet,
  allAffectedKeySet,
  context,
) {
  for (const piece of pieces) {
    const key = getPieceKey(piece);
    if (!key) continue;

    allAffectedKeySet.add(key);

    if (key !== centerKey && piece.specialType) {
      const alreadyQueued = context.nextSpecialKeys.has(key);
      const partOfCurrentWave = context.currentWaveSpecialKeys.has(key);

      if (partOfCurrentWave) {
        // TODO(cascade-refactor): Wave再設計後に除去する暫定ガード。
        // 同一Waveで既に処理予定の特殊は次Waveへ再キューしない。
      } else {
        nextSpecialPieces.push(piece);
        if (!alreadyQueued) {
          context.nextSpecialKeys.add(key);
        }
      }
    } else {
      removeSet.add(key);
    }
  }
}

function ensureCenterRemoval(centerKey, transformMap, removeSet) {
  if (!transformMap.has(centerKey)) {
    removeSet.add(centerKey);
  }
}

function finalizeSpecialWaveContext(context) {
  const removes = Array.from(context.removeSet).map((key) => {
    const [row, col] = key.split('-').map(Number);
    return { row, col };
  });

  const transforms = Array.from(context.transformMap.entries()).map(([key, to]) => {
    const [row, col] = key.split('-').map(Number);
    return { row, col, to };
  });

  // PSAQ (Post-Settle Activation Queue) 用の登録は nextSpecialPieces 側で継続する
  return {
    wave: { triggers: context.triggers, removes, transforms },
    nextSpecialPieces: context.nextSpecialPieces,
  };
}

// 必要最小限のエクスポート
export { createRemovalPlan };
